import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../App";

function GeneratorConfig(props) {
	const currencySymbol = "₺";

	const { value, onChange, errorMessages } = props;

	const { t } = useTranslation();

	const handleChange = (props) => {
		onChange(props.target.id, props.target.value);
	};

	return (
		<Stack spacing={2}>
			<TextField
				variant="standard"
				id="identifier"
				label={t("ID")}
				value={value.identifier ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("identifier")}
				helperText={errorMessages["identifier"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="name"
				label={t("Device")}
				value={value.name ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("name")}
				helperText={errorMessages["name"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="location"
				label={t("Location")}
				value={value.location ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("location")}
				helperText={errorMessages["location"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="capacities_MWh"
				label={t("Capacities (MWh)")}
				value={value.capacities_MWh ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("capacities_MWh")}
				helperText={errorMessages["capacities_MWh"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="fuel_consumption"
				label={t("Fuel consumption (m3/MWh)")}
				value={value.fuel_consumption ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("fuel_consumption")}
				helperText={errorMessages["fuel_consumption"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="steam_production"
				label={t("Steam production (T/MWh)")}
				value={value.steam_production ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("steam_production")}
				helperText={errorMessages["steam_production"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="cold_start_penalty_h"
				label={t("Cold start penalty (h)")}
				value={value.cold_start_penalty_h ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("cold_start_penalty_h")}
				helperText={errorMessages["cold_start_penalty_h"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="oil_cost_per_MWh"
				label={t("Oil consumption cost", { currency: currencySymbol })}
				value={value.oil_cost_per_MWh ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("oil_cost_per_MWh")}
				helperText={errorMessages["oil_cost_per_MWh"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="other_cost_per_MWh"
				label={t("Other costs per MWh", { currency: currencySymbol })}
				value={value.other_cost_per_MWh ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("other_cost_per_MWh")}
				helperText={errorMessages["other_cost_per_MWh"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="maintenance_cost_per_h"
				label={t("Maintenance cost", { currency: currencySymbol })}
				value={value.maintenance_cost_per_h ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("maintenance_cost_per_h")}
				helperText={errorMessages["maintenance_cost_per_h"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="personnel_cost_per_h"
				label={t("Personnel cost", { currency: currencySymbol })}
				value={value.personnel_cost_per_h ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("personnel_cost_per_h")}
				helperText={errorMessages["personnel_cost_per_h"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="wear_and_tear_per_h"
				label={t("Wear and tear", { currency: currencySymbol })}
				value={value.wear_and_tear_per_h ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("wear_and_tear_per_h")}
				helperText={errorMessages["wear_and_tear_per_h"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="other_cost_per_h"
				label={t("Other costs per hour", { currency: currencySymbol })}
				value={value.other_cost_per_h ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("other_cost_per_h")}
				helperText={errorMessages["other_cost_per_h"] ?? ""}
				fullWidth
			/>
			<TextField
				variant="standard"
				id="block_size"
				label={t("Block size (h)")}
				value={value.block_size ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("block_size")}
				helperText={errorMessages["block_size"] ?? ""}
				fullWidth
			/>
			<FormControlLabel
				control={
					<Checkbox
						id="in_operation"
						checked={value.in_operation}
						onChange={(event) => {
							onChange("in_operation", event.target.checked);
						}}
						style={{ paddingLeft: 0, paddingRight: 3 }}
					/>
				}
				label={t("In operation")}
			/>
		</Stack>
	);
}

export default function GeneratorEditPopUp(props) {
	const { open, onCloseWithoutSaving, onCloseWithUpdate } = props;

	const { t } = useTranslation();

	const [data, setData] = React.useState(props.data);
	const [errorMessages, setErrorMessages] = React.useState({});

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const {generatorApi} = React.useContext(ApiContext)
	React.useEffect(() => {
		setData(props.data);
	}, [props.data]);

	const handleCloseWithoutSaving = () => {
		setData(props.data);
		onCloseWithoutSaving();
		setErrorMessages({});
	};

	const handleFormChange = (id, value) => {
		setData((old) => ({ ...old, [id]: value }));
		setErrorMessages((old) =>
			Object.fromEntries(Object.entries(old).filter(([key]) => key !== id))
		);
	};

	const handleSave = () => {
		setErrorMessages({});

		let newData = { ...data };

		const onSuccess = (data) => {
			const updatedRow = { ...newData, id: data.url };
			onCloseWithUpdate(updatedRow);
		};

		const onError = (response) => {
			setErrorMessages(response);

			const non_field_errors = Object.entries(response)
				.filter(([key]) => key === "non_field_errors")
				.map(([key, value]) => value);
			if (non_field_errors.length > 0) {
				alert(non_field_errors.join("; "));
			}
		};

		if (!data.hasOwnProperty("id")) {
			let payload = { ...newData };

			generatorApi.create(payload, onSuccess, onError);
		} else {
			let payload = { ...newData };
			delete payload.id;

			generatorApi.update(newData.id, payload, onSuccess, onError);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleCloseWithoutSaving}
			fullScreen={fullScreen}
			fullWidth
			maxWidth={"md"}
		>
			<DialogTitle>Edit generator</DialogTitle>
			<DialogContent>
				<GeneratorConfig
					value={data}
					onChange={handleFormChange}
					errorMessages={errorMessages}
				/>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={handleCloseWithoutSaving}
					autoFocus
				>
					Cancel
				</Button>
				<Button onClick={handleSave}>Save</Button>
			</DialogActions>
		</Dialog>
	);
}
